// Conversation viewer DTOs — mirror app/routes/conversations.py.
//
// The viewer renders any of four sources (meeting / slack / email / calendar)
// as a uniform timeline of `Turn`s wrapped in a `Conversation` envelope.
// Source-specific extras ride on `metadata` JSON; the Turn component
// switches on `kind` to pick the right renderer.

export type ConversationKind =
  | "meeting"
  | "slack_channel"
  | "slack_dm"
  | "slack_group_dm"
  | "email_thread"
  | "calendar_event";

export type ConversationVisibility =
  | "personal"
  | "work_private"
  | "work_shared"
  | "public";

export type TurnKind =
  | "meeting_utterance"
  | "slack_message"
  | "email_message"
  | "calendar_description";

export type ParticipantRole = "from" | "to" | "cc" | "bcc" | "with";

export type ParticipantsOp = "and" | "or";

export interface Participant {
  role: ParticipantRole;
  person_id?: string | null;
  display_name?: string | null;
  email?: string | null;
  slack_user_id?: string | null;
}

export interface ConversationSummary {
  id: string;
  kind: ConversationKind;
  title: string | null;
  visibility: ConversationVisibility;
  started_at: string | null;
  ended_at: string | null;
  turn_count: number;
  participants: Participant[];
  metadata: Record<string, unknown>;
  captured_at: string;
  updated_at: string;
}

export interface Turn {
  id: string;
  conversation_id: string;
  source_uri: string;
  kind: TurnKind;
  speaker_label: string | null;
  speaker_person_id: string | null;
  ts: string;
  text: string;
  visibility: ConversationVisibility;
  ordinal: number;
  metadata: Record<string, unknown>;
}

export interface ConversationDetail extends ConversationSummary {
  turns: Turn[];
  turns_has_more: boolean;
  turns_next_cursor: string | null;
}

export interface ConversationListResponse {
  items: ConversationSummary[];
  has_more: boolean;
  next_offset: number | null;
}

// Per-kind metadata shapes. Narrow via `kind`.
export interface MeetingTurnMeta {
  start_ms?: number;
  end_ms?: number;
  track?: string;
  confidence?: number;
  redaction_applied?: boolean;
}

export interface SlackTurnMeta {
  message_ts?: string;
  thread_ts?: string;
  reactions?: Array<{ name: string; count: number }>;
  permalink?: string;
}

export interface EmailTurnMeta {
  message_id?: string;
  in_reply_to?: string;
  subject?: string;
  attachments?: Array<{
    filename: string;
    mime_type?: string;
    size_bytes?: number;
  }>;
}

export interface ConversationFilters {
  participants: string[]; // person IDs or 'me'
  op: ParticipantsOp;
  kinds: ConversationKind[];
  since: string | null; // ISO timestamp or preset token (last-7d, last-30d, last-90d)
  until: string | null;
  visibility: ConversationVisibility[];
  q: string | null;
}

export const DEFAULT_FILTERS: ConversationFilters = {
  participants: [],
  op: "and",
  kinds: [],
  since: "last-30d",
  until: null,
  visibility: ["work_private", "work_shared", "public"],
  q: null,
};
